import React from 'react';
import { formatMoney } from '../../../utils/utils';

// Estilos básicos para simular el documento "impreso" o reporte formal
const styles = {
    container: {
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px', // Fuente un poco más chica
        color: '#000',
        maxWidth: '800px',
        margin: '0 auto',
        border: '1px solid black',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed', // Importante para anchos fijos
    },
    cell: {
        border: '1px solid black',
        padding: '2px 2px',
        verticalAlign: 'middle',
        lineHeight: '1.15',
        height: '15px',
    },
    headerCell: {
        border: '1px solid black',
        padding: '2px 2px',
        fontWeight: 'bold',
        textAlign: 'center',
        backgroundColor: '#f8f9fa',
        verticalAlign: 'middle',
    },
    verticalTextContainer: {
        textAlign: 'center',
        fontWeight: 'bold',
        padding: '0',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 auto',
        lineHeight: '1', // Reduce espacio entre letras
        width: '100%',
    },
    verticalChar: {
        display: 'block',
        fontSize: '10px', // Letra pequeña para que quepa bien
        marginBottom: '-1px', // Apretar letras verticalmente
    },
    bold: { fontWeight: 'bold' },
    right: { textAlign: 'right' },
    center: { textAlign: 'center' },
    bgGray: { backgroundColor: '#f0f0f0' },
};

const ResumenPagoAuxiliar = ({ row, periodo, modo = 'fm', factorRetencion, configTextos }) => {
    // modo: 'adelanto' (solo 1ra quincena) o 'fm' (fin de mes, ambas)
    if (!row) return null;

    // --- Lógica de Fechas y Textos ---
    const [anioStr, mesStr] = (periodo || "").split("-");
    const anio = parseInt(anioStr || 0, 10);
    const mes = parseInt(mesStr || 1, 10);

    // Mes actual
    const meses = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];
    const nombreMes = meses[mes - 1] || "";
    const labelPeriodo = `${nombreMes}-${anioStr ? anioStr.slice(2) : ""}`;

    // Helper para renderizar negritas básicas
    const renderMarkdown = (text) => {
        if (!text) return "";
        // Reemplaza **texto** por <b>texto</b>
        let html = text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
        // También soportar \n por <br/>
        html = html.replace(/\n/g, '<br/>');
        return <span dangerouslySetInnerHTML={{ __html: html }} />;
    };

    // Mes anterior (para el inicio del periodo de turno Q1)
    const fechaActual = new Date(anio, mes - 1, 1);
    const fechaPrev = new Date(fechaActual);
    fechaPrev.setMonth(fechaActual.getMonth() - 1);
    const mesPrevNum = fechaPrev.getMonth() + 1;
    // const nombreMesPrev = meses[fechaPrev.getMonth()];

    // Textos de rangos de fechas
    // Q1: 26 del mes anterior al 10 del mes actual
    const textoRangoQ1 = `entre el 26.${mesPrevNum} y el 10.${mes}.${anioStr ? anioStr.slice(2) : ""}`;

    // Q2: 11 del mes actual al 25 del mes actual
    const textoRangoQ2 = `entre el 11.${mes} y el 25.${mes}.${anioStr ? anioStr.slice(2) : ""}`;

    // Fechas de Pago
    const getDiaPagoAdelanto = (y, m) => {
        // Buscar desde el 15 hacia atrás hasta el 10
        for (let d = 15; d >= 10; d--) {
            const date = new Date(y, m - 1, d);
            const dayOfWeek = date.getDay(); // 0: Dom, 6: Sab
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                return d.toString();
            }
        }
        return "15"; // fallback
    };

    const diaPagoAdelanto = getDiaPagoAdelanto(anio, mes);
    
    const getDiaPagoFM = (y, m) => {
        // Obtenemos el último día del mes
        let lastDay = new Date(y, m, 0);
        
        // Mientras sea Sábado (6) o Domingo (0), retrocedemos un día
        while (lastDay.getDay() === 0 || lastDay.getDay() === 6) {
            lastDay.setDate(lastDay.getDate() - 1);
        }
        
        return lastDay.getDate().toString();
    };
    const diaPagoFM = getDiaPagoFM(anio, mes);

    // --- Lógica de Retención ---
    // Si viene factor por prop, lo usamos (ej: 0.855 o 0.8475)
    // Si no, lo calculamos por año.
    let factorLiquido = factorRetencion;
    let tasaRetencion;

    if (!factorLiquido) {
        // Fallback lógica interna
        tasaRetencion = 0.145;
        if (anio >= 2026) tasaRetencion = 0.1525;
        factorLiquido = 1 - tasaRetencion;
    } else {
        // Derivar tasa del factor
        tasaRetencion = 1 - factorLiquido;
    }

    // Formateo porcentaje (ej: 0.1525 -> 15,25%)
    // Math.round para evitar 14.4999999%
    const porcRetencionTexto = (Math.round(tasaRetencion * 10000) / 100).toLocaleString('es-CL') + "%";

    // --- Extracción y Cálculo de Valores ---
    const turnosTotalQty = row.cantidad_turnos_total || 0;

    // Q1
    const q1_TurnosQty = row.cantidad_turnos_adelanto || 0;
    const q1_Bruto = row.valor_turnos_adelanto || 0;
    const q1_LiquidoCalc = q1_Bruto * factorLiquido;
    const q1_Extras = row.horas_extras_adelanto || 0;
    const q1_ExtrasLiquido = q1_Extras * factorLiquido;
    const q1_Mov = row.locomocion_adelanto || 0;
    const q1_Alim = row.colacion_adelanto || 0;
    const q1_OtrosSuma = row.otros_suma_adelanto || 0;
    const q1_OtrosResta = row.otros_resta_adelanto || 0;
    const q1_Prestamos = row.prestamos_adelanto || 0;
    const q1_Vacaciones = row.vacaciones_adelanto || 0;

    // Q2
    const q2_TurnosQty = turnosTotalQty - q1_TurnosQty;
    const q2_Bruto = (row.valor_turnos_total || 0) - q1_Bruto;
    const q2_LiquidoCalc = q2_Bruto * factorLiquido;
    const q2_Extras = (row.horas_extras_total || 0) - q1_Extras;
    const q2_ExtrasLiquido = q2_Extras * factorLiquido;
    const q2_Mov = (row.locomocion_total || 0) - q1_Mov;
    const q2_Alim = (row.colacion_total || 0) - q1_Alim;
    const q2_OtrosSuma = (row.otros_suma_fm || 0);
    const q2_OtrosResta = row.otros_resta_fm || 0;
    const q2_Prestamos = row.prestamos_fm || 0;
    const q2_Vacaciones = row.vacaciones_fm || 0;

    // Calculadoras
    const calcularLiquidoFinalQ = (brutoTurnos, extras, mov, alim, otrosSuma, otrosResta, prestamo, vacaciones) => {
        const liqTurnos = brutoTurnos * factorLiquido;
        const liqExtras = extras * factorLiquido;
        return liqTurnos + liqExtras + mov + alim + otrosSuma - otrosResta - prestamo + vacaciones;
    };

    const totalPagarQ1 = calcularLiquidoFinalQ(q1_Bruto, q1_Extras, q1_Mov, q1_Alim, q1_OtrosSuma, q1_OtrosResta, q1_Prestamos, q1_Vacaciones);
    const totalPagarQ2 = calcularLiquidoFinalQ(q2_Bruto, q2_Extras, q2_Mov, q2_Alim, q2_OtrosSuma, q2_OtrosResta, q2_Prestamos, q2_Vacaciones);
    const totalBoleta = (row.valor_turnos_total || 0) + (row.horas_extras_total || 0);

    // Helper para renderizar texto vertical letra por letra
    const VerticalText = ({ text }) => (
        <div style={styles.verticalTextContainer}>
            {text.split('').map((char, index) => (
                <span key={index} style={styles.verticalChar}>{char}</span>
            ))}
        </div>
    );

    // --- Contar filas para rowSpan dinámico ---
    const itemsQ1 = [q1_Extras, q1_Mov, q1_Alim, q1_OtrosSuma, q1_OtrosResta, q1_Prestamos, q1_Vacaciones];
    const rowSpanQ1 = 3 + itemsQ1.filter(v => v !== 0).length; // 3 = Turnos + Bruto + Liquido

    const itemsQ2 = [q2_Extras, q2_Mov, q2_Alim, q2_OtrosSuma, q2_OtrosResta, q2_Prestamos, q2_Vacaciones];
    const rowSpanQ2 = 3 + itemsQ2.filter(v => v !== 0).length; // 3 = Turnos + Bruto + Liquido

    const RowItem = ({ label, value }) => {
        if (!value || value === 0) return null;
        return (
            <tr>
                <td style={styles.cell} colSpan={2}>{label}</td>
                <td style={{ ...styles.cell, ...styles.bold, ...styles.right }}>{formatMoney(value)}</td>
            </tr>
        );
    };

    return (
        <div style={styles.container}>
            <table style={styles.table}>
                {/* Definir anchos de columnas para asegurar alineación */}
                <colgroup>
                    <col style={{ width: '50px' }} /> {/* Col para PRIMERA/SEGUNDA */}
                    <col style={{ width: '50px' }} /> {/* Col para QUINCENA */}
                    <col style={{ width: 'auto' }} />
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '80px' }} />
                </colgroup>
                <tbody>
                    {/* ENCABEZADO: NOMBRE */}
                    <tr>
                        <td style={{ ...styles.headerCell, fontSize: '14px', fontWeight: 'bold' }} colSpan={5}>
                            {row.nombre_auxiliar} {row.apepat_auxiliar} {row.apemat_auxiliar}
                        </td>
                    </tr>

                    {/* TÍTULO */}
                    <tr>
                        <td style={{ ...styles.headerCell, backgroundColor: '#fff' }} colSpan={5}>
                            DETALLE DE PAGO DE {labelPeriodo}
                        </td>
                    </tr>

                    {/* INFO TURNOS TOTALES */}
                    {/* INFO TURNOS TOTALES - Solo en Fin de Mes */}
                    {modo === 'fm' && (
                        <tr>
                            <td style={styles.cell} colSpan={4}>
                                N° turnos realizados en {labelPeriodo} ({textoRangoQ1.split('y')[0]} al 25.{mes})
                            </td>
                            <td style={{ ...styles.cell, ...styles.bold, ...styles.right }}>
                                {turnosTotalQty}
                            </td>
                        </tr>
                    )}

                    {/* --- PRIMERA QUINCENA --- */}
                    <tr>
                        {/* Columna 1: PRIMERA */}
                        <td rowSpan={rowSpanQ1} style={{ ...styles.cell, padding: 0, verticalAlign: 'middle' }}>
                            <VerticalText text="PRIMERA" />
                        </td>
                        {/* Columna 2: QUINCENA */}
                        <td rowSpan={rowSpanQ1} style={{ ...styles.cell, padding: 0, verticalAlign: 'middle' }}>
                            <VerticalText text="QUINCENA" />
                        </td>

                        <td style={styles.cell} colSpan={2}>
                            N° turnos cancelados en el Anticipo de {labelPeriodo} ({textoRangoQ1})
                        </td>
                        <td style={{ ...styles.cell, ...styles.bold, ...styles.right }}>
                            {q1_TurnosQty}
                        </td>
                    </tr>

                    <tr>
                        <td style={styles.cell} colSpan={2}>valor bruto</td>
                        <td style={{ ...styles.cell, ...styles.bold, ...styles.right }}>
                            {formatMoney(q1_Bruto)}
                        </td>
                    </tr>

                    <tr>
                        <td style={styles.cell} colSpan={2}>
                            valor líquido (menos {porcRetencionTexto})*
                        </td>
                        <td style={{ ...styles.cell, ...styles.bold, ...styles.right }}>
                            {formatMoney(q1_LiquidoCalc)}
                        </td>
                    </tr>

                    <RowItem label="Horas Extras liquido" value={q1_ExtrasLiquido} />
                    <RowItem label="Movilización" value={q1_Mov} />
                    <RowItem label="Alimentación" value={q1_Alim} />
                    <RowItem label="otros suma (+)" value={q1_OtrosSuma} />
                    <RowItem label="otros resta (-)" value={q1_OtrosResta} />
                    <RowItem label="descuento por préstamo (-)" value={q1_Prestamos} />
                    <RowItem label="dias libres / vacaciones (+)" value={q1_Vacaciones} />

                    {/* --- TOTAL ADELANTO --- */}
                    {modo === 'adelanto' && (
                        <tr>
                            <td style={{ ...styles.cell, ...styles.bold, textAlign: 'right', fontSize: '13px' }} colSpan={4}>
                                total a pagar el {diaPagoAdelanto}-{mesStr}-{anioStr ? anioStr.slice(2) : ""}
                            </td>
                            <td style={{ ...styles.cell, ...styles.bold, ...styles.right, fontSize: '13px' }}>
                                {formatMoney(totalPagarQ1)}
                            </td>
                        </tr>
                    )}

                    {/* SI ES MODO ADELANTO, NO RENDERING SEGUNDA QUINCENA */}
                    {modo === 'fm' && (
                        <>
                            {/* --- SEGUNDA QUINCENA --- */}
                            <tr>
                                <td rowSpan={rowSpanQ2} style={{ ...styles.cell, padding: 0, verticalAlign: 'middle' }}>
                                    <VerticalText text="SEGUNDA" />
                                </td>
                                <td rowSpan={rowSpanQ2} style={{ ...styles.cell, padding: 0, verticalAlign: 'middle' }}>
                                    <VerticalText text="QUINCENA" />
                                </td>

                                <td style={styles.cell} colSpan={2}>
                                    N° turnos a cancelar en la segunda quincena de {labelPeriodo} ({textoRangoQ2})
                                </td>
                                <td style={{ ...styles.cell, ...styles.bold, ...styles.right }}>
                                    {q2_TurnosQty}
                                </td>
                            </tr>

                            <tr>
                                <td style={styles.cell} colSpan={2}>valor bruto</td>
                                <td style={{ ...styles.cell, ...styles.bold, ...styles.right }}>
                                    {formatMoney(q2_Bruto)}
                                </td>
                            </tr>

                            <tr>
                                <td style={styles.cell} colSpan={2}>
                                    valor líquido (menos {porcRetencionTexto})*
                                </td>
                                <td style={{ ...styles.cell, ...styles.bold, ...styles.right }}>
                                    {formatMoney(q2_LiquidoCalc)}
                                </td>
                            </tr>

                            <RowItem label="Horas Extras liquido" value={q2_ExtrasLiquido} />
                            <RowItem label="movilización (+)" value={q2_Mov} />
                            <RowItem label="alimentación (+)" value={q2_Alim} />
                            <RowItem label="otros suma (+)" value={q2_OtrosSuma} />
                            <RowItem label="otros resta (-)" value={q2_OtrosResta} />
                            <RowItem label="descuento por préstamo (-)" value={q2_Prestamos} />
                            <RowItem label="dias libres / vacaciones (+)" value={q2_Vacaciones} />

                            {/* --- TOTAL A PAGAR FM --- */}
                            <tr>
                                <td style={{ ...styles.cell, ...styles.bold, textAlign: 'right', fontSize: '13px' }} colSpan={4}>
                                    total a pagar el {diaPagoFM}-{mesStr}-{anioStr ? anioStr.slice(2) : ""}
                                </td>
                                <td style={{ ...styles.cell, ...styles.bold, ...styles.right, fontSize: '13px' }}>
                                    {formatMoney(totalPagarQ2)}
                                </td>
                            </tr>
                        </>
                    )}

                    {/* --- VALOR BOLETA --- */}
                    {modo === 'fm' && (
                        <tr>
                            <td style={{ ...styles.cell, ...styles.bold, textAlign: 'right', fontSize: '13px' }} colSpan={4}>
                                valor de la boleta de {labelPeriodo}
                            </td>
                            <td style={{ ...styles.cell, ...styles.bold, ...styles.right, fontSize: '13px' }}>
                                {formatMoney(totalBoleta)}
                            </td>
                        </tr>
                    )}

                    {/* --- NOTAS INFORMATIVAS DINÁMICAS --- */}
                    {configTextos ? (
                        <>
                            {configTextos.texto1 && (
                                <tr>
                                    <td style={{ ...styles.cell, fontSize: '15px', padding: '5px' }} colSpan={5}>
                                        {renderMarkdown(configTextos.texto1)}
                                    </td>
                                </tr>
                            )}
                            {configTextos.texto2 && (
                                <tr>
                                    <td style={{ ...styles.cell, fontSize: '15px', padding: '5px' }} colSpan={5}>
                                        {renderMarkdown(configTextos.texto2)}
                                    </td>
                                </tr>
                            )}
                            {configTextos.texto3 && (
                                <tr>
                                    <td style={{ ...styles.cell, fontSize: '15px', padding: '5px', borderTop: '2px solid black' }} colSpan={5}>
                                        {renderMarkdown(configTextos.texto3)}
                                    </td>
                                </tr>
                            )}
                        </>
                    ) : (
                        <>
                            <tr>
                                <td style={{ ...styles.cell, fontSize: '15px', padding: '5px' }} colSpan={5}>
                                    Como personal de salud, es necesario que te vacunes contra la influenza y covid 19. Puedes hacerlo en el centro de salud más cercano a tu domicilio. Si para ello, necesitas un certificado que acredite que eres personal de salud, puedes solicitarlo a nuestra secretaria: <span style={styles.bold}>Anita Tovaria +56 9 9309 6748</span>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.cell, fontSize: '15px', padding: '5px' }} colSpan={5}>
                                    *Información importante: según disposición de Impuestos Internos hasta el 31-12-25 la retención de impuesto es de un 14,5%. A partir del 01-01-26 será de un 15,25%.
                                </td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.cell, fontSize: '15px', padding: '5px', borderTop: '2px solid black' }} colSpan={5}>
                                    <span style={styles.bold}>OJO</span> Cualquier duda, envíe un wa a Sra M. Angélica Palma al +56 9 92305037. <span style={styles.bold}>Por el contrario, si está de acuerdo con esta información, por favor conteste este wa con un OK</span>
                                </td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default ResumenPagoAuxiliar;
